import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { getSessionUser, getSessionUserId, isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { ENDPOINTS as GENERAL_ENDPOINTS } from '../../../../General/Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import AlertDialog from '../../Shared/AlertDialog';

export default function AreaNegocioCursoManager({ idAreaNegocio, onClose }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allCourses, setAllCourses] = useState([]);
    const [sharedCourseIds, setSharedCourseIds] = useState(new Set());
    const [currentAssigned, setCurrentAssigned] = useState([]);
    const [originalAssigned, setOriginalAssigned] = useState([]);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [idEmpresa, setIdEmpresa] = useState(null);
    const [query, setQuery] = useState('');
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        // El usuario dijo que se cierre el modal primero
        if (onClose) onClose();
        setAlertConfig({ open: true, message, variant, title });
    };

    useEffect(() => {
        const fetchEmpresa = async () => {
            try {
                const user = getSessionUser();
                if (user && user.idEmpresa) {
                    setIdEmpresa(user.idEmpresa);
                    return;
                }
                const userId = getSessionUserId();
                if (!userId) {
                    console.warn("[AreaNegocioCursoManager] No se encontró el ID de usuario.");
                    return;
                }
                const res = await axios.get(GENERAL_ENDPOINTS.empresasUsuario(userId));
                const empresas = res.data || [];
                if (empresas.length > 0) {
                    const foundId = empresas[0].idEmpresa || empresas[0].IdEmpresa;
                    console.log("[AreaNegocioCursoManager] Empresa resuelta:", foundId);
                    setIdEmpresa(foundId);
                } else {
                    console.warn("[AreaNegocioCursoManager] El usuario no tiene empresas asociadas.");
                }
            } catch (e) {
                console.error("[AreaNegocioCursoManager] Error fetching company:", e);
            }
        };
        fetchEmpresa();
    }, []);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            // Priority 1: All courses
            const resAll = await axios.get(ENDPOINTS.cursos);
            const courses = (resAll.data || []).map(Models.cursos);

            // Fetch shared courses for this company
            const empId = idEmpresa || getSessionEmpresaId();
            let sharedCourses = [];
            let sharedIds = new Set();
            if (empId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.cursoEmpresaCompartidaRecibidosEnriquecidos(empId));
                    sharedCourses = (resShared.data || []).map(item => {
                        const mapped = Models.cursos(item);
                        mapped.empresaNombre = item.nombreEmpresaPropietaria || item.NombreEmpresaPropietaria || item.nombreEmpresaOrigen || item.NombreEmpresaOrigen || 'General';
                        mapped.activo = true;
                        mapped.terminado = true;
                        return mapped;
                    });
                    sharedIds = new Set(sharedCourses.map(c => c.idCurso));
                } catch (err) {
                    console.error('[AreaNegocioCursoManager] Error loading shared courses:', err);
                }
            }
            setSharedCourseIds(sharedIds);

            // Merge own courses and shared courses
            const mergedCourses = [...courses];
            sharedCourses.forEach(sc => {
                if (!mergedCourses.some(c => Number(c.idCurso) === Number(sc.idCurso))) {
                    mergedCourses.push(sc);
                }
            });
            setAllCourses(mergedCourses);

            // Priority 2: Assigned courses (If context available)
            if (empId && idAreaNegocio) {
                const resAssigned = await axios.get(ENDPOINTS.areaNegocioCursoByArea(idAreaNegocio, empId));
                const assignedData = (resAssigned.data || []).map(item => {
                    const courseId = item.idCurso || item.IdCurso;
                    const c = mergedCourses.find(x => Number(x.idCurso) === Number(courseId));
                    return {
                        idCurso: courseId,
                        nombre: item.NombreCurso || item.nombre,
                        empresaNombre: (c && c.empresaNombre) ? c.empresaNombre : 'General'
                    };
                });
                setCurrentAssigned(assignedData);
                setOriginalAssigned(assignedData);
            } else {
                setCurrentAssigned([]);
                setOriginalAssigned([]);
            }

        } catch (error) {
            console.error('[AreaNegocioCursoManager] Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }, [idAreaNegocio, idEmpresa]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const assignedIds = useMemo(() => new Set(currentAssigned.map(c => c.idCurso)), [currentAssigned]);
    const availableCourses = useMemo(() => {
        const isSuperAdmin = isSessionSuperAdmin();
        const sessionEmpresaId = idEmpresa || getSessionEmpresaId();

        let available = allCourses.filter(c => {
            // 1. No estar asignado ya
            if (assignedIds.has(c.idCurso)) return false;
            // 2. Estar activo y terminado
            if (!c.activo || !c.terminado) return false;

            // 3. Reglas multi-tenant:
            if (isSuperAdmin) return true;

            const isOwn = c.idEmpresa === sessionEmpresaId;
            const isShared = sharedCourseIds.has(c.idCurso);

            return isOwn || isShared;
        });

        // Filter by query
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            available = available.filter(c => 
                c.nombre.toLowerCase().includes(q) || 
                (c.empresaNombre || '').toLowerCase().includes(q)
            );
        }
        return available;
    }, [allCourses, currentAssigned, sharedCourseIds, idEmpresa, query]);

    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    };

    const handleDragOver = (e, target) => {
        e.preventDefault();
        setDragOverColumn(target);
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);
        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === targetColumn) return;

        if (targetColumn === 'assigned') {
            setCurrentAssigned(prev => [...prev, item]);
        } else {
            setCurrentAssigned(prev => prev.filter(c => c.idCurso !== item.idCurso));
        }
    };

    const handleSave = async () => {
        if (!idEmpresa) {
            triggerAlert("No se puede guardar: falta el contexto de la empresa.");
            return;
        }
        setSaving(true);
        try {
            const currentIds = new Set(currentAssigned.map(a => a.idCurso));
            const originalIds = new Set(originalAssigned.map(a => a.idCurso));

            const promises = [];

            // 1. Asignar nuevos
            for (const item of currentAssigned) {
                if (!originalIds.has(item.idCurso)) {
                    promises.push(axios.post(ENDPOINTS.areaNegocioCursoAsignar, {
                        IdAreaNegocio: parseInt(idAreaNegocio),
                        IdCurso: item.idCurso,
                        IdEmpresa: idEmpresa,
                        NombreCurso: item.nombre,
                        FechaAsignacion: new Date().toISOString(),
                        Activo: true
                    }));
                }
            }

            // 2. Desasignar eliminados
            for (const item of originalAssigned) {
                if (!currentIds.has(item.idCurso)) {
                    promises.push(axios.post(ENDPOINTS.areaNegocioCursoDesasignar, {
                        IdAreaNegocio: parseInt(idAreaNegocio),
                        IdCurso: item.idCurso,
                        IdEmpresa: idEmpresa,
                        Activo: false
                    }));
                }
            }

            await Promise.all(promises);
            await loadData();
            triggerAlert("Cambios guardados correctamente.", 'success', 'Éxito');
        } catch (error) {
            console.error("Error updating assignment:", error);
            triggerAlert("No se pudo completar la operación.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mini-crud">
            <div className="mini-topbar">
                <div>
                    <h3 className="roles-title" style={{ margin: 0 }}>Asignación de Cursos por Área</h3>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        Arrastra los cursos para asignar o desasignar.
                        <br />
                        <span style={{ color: '#827717', fontWeight: '500' }}>
                            <strong>Configuración Permanente:</strong> Los cursos en la columna derecha se fijarán al área.
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ padding: '0 0.5rem 1rem 0.5rem' }}>
                <input
                    className="roles-search"
                    placeholder="Buscar curso disponible..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ width: '100%', marginBottom: 0 }}
                />
            </div>

            {loading ? (
                <div style={{ padding: '100px 40px', textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="catalog-loading">Cargando catálogo...</div>
                </div>
            ) : (
                <div className="dnd-container" style={{ position: 'relative' }}>
                    {saving && (
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(255,255,255,0.6)', zIndex: 10,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: '8px', backdropFilter: 'blur(2px)'
                        }}>
                            <div className="saving-spinner">Guardando...</div>
                        </div>
                    )}

                    {/* Disponibles */}
                    <div
                        className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'available')}
                        onDrop={(e) => handleDrop(e, 'available')}
                    >
                        <div className="dnd-header">Cursos Disponibles</div>
                        {availableCourses.length === 0 ? (
                            <div className="dnd-empty">No hay cursos disponibles.</div>
                        ) : (
                            availableCourses.map(c => (
                                <div
                                    key={c.idCurso}
                                    className="dnd-item"
                                    draggable={!saving}
                                    onDragStart={(e) => handleDragStart(e, c, 'available')}
                                >
                                    <span>{`(${c.empresaNombre || 'General'}): ${c.nombre}`}</span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentAssigned(prev => [...prev, c])}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.2em',
                                            color: '#28a745',
                                            cursor: 'pointer',
                                            padding: '0 5px'
                                        }}
                                        title="Asignar"
                                    >＋</button>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Asignados */}
                    <div
                        className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'assigned')}
                        onDrop={(e) => handleDrop(e, 'assigned')}
                    >
                        <div className="dnd-header">Cursos del Área</div>
                        {currentAssigned.length === 0 ? (
                            <div className="dnd-empty">Arrastra cursos aquí para fijarlos al área.</div>
                        ) : (
                            currentAssigned.map(c => (
                                <div
                                    key={c.idCurso}
                                    className="dnd-item"
                                    draggable={!saving}
                                    onDragStart={(e) => handleDragStart(e, c, 'assigned')}
                                    style={{ borderLeft: '4px solid #43a047' }}
                                >
                                    <span>{`(${c.empresaNombre || 'General'}): ${c.nombre}`}</span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentAssigned(prev => prev.filter(x => x.idCurso !== c.idCurso))}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.2em',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            padding: '0 5px'
                                        }}
                                        title="Desasignar"
                                    >×</button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
                <button className="btn-cancel" onClick={onClose} type="button">Cerrar</button>
                <button
                    className="btn-action"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                    type="button"
                >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
